import { Toggle } from "../../components/Toggle";
import { t } from "../../i18n";
import type { Attachment, User, UserMemory } from "@lumen/shared";
import {
  Brain,
  Camera,
  Check,
  LoaderCircle,
  Pencil,
  Save,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { api, authUrl } from "../../api";
import { setLanguage, type Language } from "../../i18n";
import { SettingsPage } from "./SettingsPage";
export { SettingsPage } from "./SettingsPage";

export function ProfilePage({
  user,
  onUserChange,
}: {
  user: User;
  onUserChange: (user: User) => void;
}) {
  const [name, setName] = useState(user.name);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl ?? "");
  const [language, setLanguagePreference] = useState<Language | "">(
    user.language ?? "",
  );
  const [memoryEnabled, setMemoryEnabled] = useState(user.memoryEnabled);
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [editingMemoryId, setEditingMemoryId] = useState<string | null>(null);
  const [memoryDraft, setMemoryDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [passkeys, setPasskeys] = useState<
    Array<{
      id: string;
      name: string;
      deviceType: string | null;
      backedUp: boolean;
      createdAt: string;
      lastUsedAt: string | null;
    }>
  >([]);
  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const image = await api<Attachment>("/uploads", { method: "POST", body });
      setAvatarUrl(image.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Upload failed"));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const updated = await api<User>("/me", {
        method: "PATCH",
        body: JSON.stringify({
          name,
          avatarUrl: avatarUrl || null,
          language: language || null,
          memoryEnabled,
        }),
      });
      onUserChange(updated);
      await setLanguage(updated.language ?? updated.defaultLanguage);
      setSaved(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("Profile could not be saved"),
      );
    } finally {
      setBusy(false);
    }
  };
  const changeLanguage = async (value: Language | "") => {
    setLanguagePreference(value);
    await setLanguage(value || user.defaultLanguage);
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const updated = await api<User>("/me", {
        method: "PATCH",
        body: JSON.stringify({ language: value || null }),
      });
      onUserChange(updated);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Language could not be saved");
    } finally {
      setBusy(false);
    }
  };
  const loadMemories = () =>
    api<UserMemory[]>("/me/memories")
      .then(setMemories)
      .catch((e) =>
        setError(
          e instanceof Error ? e.message : "Memories could not be loaded",
        ),
      );
  useEffect(() => {
    void loadMemories();
    void api<typeof passkeys>("/me/passkeys")
      .then(setPasskeys)
      .catch(() => {});
  }, []);
  const addPasskey = async () => {
    const name = prompt(t("Passkey name"), t("My passkey"));
    if (!name) return;
    setBusy(true);
    setError("");
    try {
      const registration = await api<{
        challengeId: string;
        options: Parameters<typeof startRegistration>[0]["optionsJSON"];
      }>("/me/passkeys/options", { method: "POST" });
      const response = await startRegistration({
        optionsJSON: registration.options,
      });
      await api("/me/passkeys/verify", {
        method: "POST",
        body: JSON.stringify({
          challengeId: registration.challengeId,
          response,
          name,
        }),
      });
      setPasskeys(await api<typeof passkeys>("/me/passkeys"));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : t("Passkey setup failed"),
      );
    } finally {
      setBusy(false);
    }
  };
  const removePasskey = async (id: string) => {
    await api(`/me/passkeys/${id}`, { method: "DELETE" });
    setPasskeys((current) => current.filter((passkey) => passkey.id !== id));
  };
  const removeMemory = async (id: string) => {
    setError("");
    try {
      await api(`/me/memories/${id}`, { method: "DELETE" });
      setMemories((current) => current.filter((memory) => memory.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Memory could not be deleted");
    }
  };
  const editMemory = (memory: UserMemory) => {
    setEditingMemoryId(memory.id);
    setMemoryDraft(memory.content);
  };
  const saveMemory = async () => {
    if (!editingMemoryId || !memoryDraft.trim()) return;
    setError("");
    try {
      const updated = await api<UserMemory>(`/me/memories/${editingMemoryId}`, {
        method: "PATCH",
        body: JSON.stringify({ content: memoryDraft }),
      });
      setMemories((current) =>
        current.map((memory) => (memory.id === updated.id ? updated : memory)),
      );
      setEditingMemoryId(null);
      setMemoryDraft("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Memory could not be updated");
    }
  };
  const clearMemories = async () => {
    if (!confirm("Delete all saved memories? This cannot be undone.")) return;
    setError("");
    try {
      await api("/me/memories", { method: "DELETE" });
      setMemories([]);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Memories could not be cleared",
      );
    }
  };
  return (
    <SettingsPage
      title={t("Profile")}
      description="Manage the name, picture, and language shown throughout Lumen."
    >
      <section className="panel max-w-2xl p-5 sm:p-6">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <div className="relative grid size-24 shrink-0 place-items-center overflow-hidden rounded-full bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-200">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={t("Profile")}
                className="size-full object-cover"
              />
            ) : (
              <UserRound size={34} />
            )}
            <label
              title={t("Upload profile image")}
              className="absolute inset-x-0 bottom-0 flex cursor-pointer justify-center bg-black/55 py-1.5 text-white"
            >
              <Camera size={16} />
              <input
                className="sr-only"
                type="file"
                accept="image/*"
                onChange={(e) => void upload(e.target.files?.[0])}
              />
            </label>
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <label>
              <span className="label">{t("Display name")}</span>
              <input
                className="field"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div>
              <span className="label">{t("Email")}</span>
              <div className="field cursor-not-allowed bg-stone-50 text-stone-500 dark:bg-white/3">
                {user.email}
              </div>
              <p className="mt-1.5 text-xs text-stone-400">
                {t("Your email is managed by your OIDC identity provider.")}
              </p>
            </div>
            <label className="block">
              <span className="label">{t("Language")}</span>
              <select
                className="field"
                disabled={busy}
                value={language}
                onChange={(event) =>
                  void changeLanguage(event.target.value as Language | "")
                }
              >
                <option value="">
                  {t("Use website default ({{language}})", {
                    language:
                      user.defaultLanguage === "fr" ? "Français" : t("English"),
                  })}
                </option>
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </label>
          </div>
        </div>
        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-300">{error}</p>
        )}
        <div className="mt-5 flex justify-end">
          <button
            className="button-primary"
            disabled={busy || !name.trim()}
            onClick={() => void save()}
          >
            {busy ? (
              <LoaderCircle className="animate-spin" size={16} />
            ) : saved ? (
              <Check size={16} />
            ) : (
              <Save size={16} />
            )}{" "}
            {saved ? "Saved" : t("Save profile")}
          </button>
        </div>
        <div className="mt-6 flex items-center justify-between border-t border-stone-200 pt-5 dark:border-white/10">
          <div>
            <strong className="block text-sm">{t("Sign out")}</strong>
            <span className="text-xs text-stone-400">
              {t("End this session on this device.")}
            </span>
          </div>
          <a className="button" href={authUrl("logout")}>
            {t("Sign out")}
          </a>
        </div>
      </section>
      <section className="panel mt-5 max-w-2xl p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-bold">{t("Passkeys")}</h3>
            <p className="mt-1 text-sm text-stone-500">
              {t("Use a device passkey for phishing-resistant sign-in.")}
            </p>
          </div>
          <button
            className="button-primary"
            disabled={busy}
            onClick={() => void addPasskey()}
          >
            {t("Add passkey")}
          </button>
        </div>
        <div className="mt-4 space-y-2">
          {passkeys.map((passkey) => (
            <div
              key={passkey.id}
              className="flex items-center justify-between rounded-xl bg-stone-50 p-3 dark:bg-white/4"
            >
              <div>
                <strong className="text-sm">{passkey.name}</strong>
                <p className="text-xs text-stone-400">
                  {passkey.backedUp ? t("Synced passkey") : t("Device passkey")}
                </p>
              </div>
              <button
                className="text-stone-400 hover:text-red-600"
                title={t("Delete passkey")}
                onClick={() => void removePasskey(passkey.id)}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {!passkeys.length && (
            <p className="text-sm text-stone-400">
              {t("No passkeys registered.")}
            </p>
          )}
        </div>
      </section>
      <section className="panel mt-5 max-w-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-950/30 dark:text-violet-300">
            <Brain size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-bold">{t("Memory")}</h3>
                <p className="mt-1 text-sm leading-5 text-stone-500">
                  {t(
                    "Allow AI to remember durable preferences and facts you share for future conversations.",
                  )}
                </p>
              </div>
              <label className="flex shrink-0 items-center gap-2 text-sm font-semibold">
                <Toggle
                  checked={memoryEnabled}
                  onChange={(event) => setMemoryEnabled(event.target.checked)}
                />{" "}
                {memoryEnabled ? t("On") : t("Off")}
              </label>
            </div>
            <p className="mt-3 text-xs leading-5 text-stone-400">
              {t(
                "Memory is off by default. Turning it off stops memories from being used or added, but does not delete what is already stored.",
              )}
            </p>
          </div>
        </div>
        <div className="mt-5 border-t border-stone-200 pt-4 dark:border-white/10">
          <div className="mb-3 flex items-center justify-between">
            <strong className="text-sm">
              {t("savedMemory", { count: memories.length })}
            </strong>
            {memories.length > 0 && (
              <button
                className="text-xs text-red-600 hover:underline"
                onClick={() => void clearMemories()}
              >
                {t("Clear all")}
              </button>
            )}
          </div>
          {memories.length ? (
            <div className="space-y-2">
              {memories.map((memory) => (
                <div
                  key={memory.id}
                  className="flex items-start gap-2 rounded-xl bg-stone-50 p-3 text-sm dark:bg-white/4"
                >
                  {editingMemoryId === memory.id ? (
                    <>
                      <input
                        className="field min-w-0 flex-1 py-1.5"
                        maxLength={500}
                        autoFocus
                        value={memoryDraft}
                        onChange={(event) => setMemoryDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") void saveMemory();
                          if (event.key === "Escape") setEditingMemoryId(null);
                        }}
                      />
                      <button
                        className="shrink-0 text-lumen-600"
                        title={t("Save memory")}
                        disabled={!memoryDraft.trim()}
                        onClick={() => void saveMemory()}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        className="shrink-0 text-stone-400"
                        title={t("Cancel editing")}
                        onClick={() => setEditingMemoryId(null)}
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="min-w-0 flex-1 leading-5">
                        {memory.content}
                      </p>
                      <button
                        className="shrink-0 text-stone-400 hover:text-lumen-600"
                        title={t("Edit memory")}
                        onClick={() => editMemory(memory)}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="shrink-0 text-stone-400 hover:text-red-600"
                        title={t("Delete memory")}
                        onClick={() => void removeMemory(memory.id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-400">
              {t("No memories saved yet.")}
            </p>
          )}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            className="button-primary"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? (
              <LoaderCircle className="animate-spin" size={16} />
            ) : (
              <Save size={16} />
            )}{" "}
            {t("Save memory setting")}
          </button>
        </div>
      </section>
    </SettingsPage>
  );
}
